import type { ReceiverInput } from "@pdfking/functionpool"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import type { SinglePagePdf } from "../../types/files/cached/SinglePagePdf"
import type { TaskPage } from "../../types/tasks/input/TaskPage"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskMergeOptions } from "../../types/tasks/options/TaskMergeOptions"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"
import { PDFMerge } from "@pdfking/renderer-default"
import { SortBy } from "@pdfking/tfunc"
import { GenerateFile } from "../../files/GenerateFile"
import { RequestBlob } from "../../files/RequestBlob"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"

export const TaskMerge = async function* (
  engine: PdfEngine,
  _task: TaskMergeOptions,
  emit: TrackingCallback,
  messages: ReceiverInput<TaskPage>
): AsyncGenerator<TaskResult> {
  const pages: SinglePagePdf[] = []
  let done = 0

  const emitter = (done: number, total: number) =>
    emit({
      done,
      name: TrackedTaskName.MERGE_PDF,
      total,
    })

  void emitter(0, 1)

  async function* pagesGenerator() {
    for await (const { content, page_count, page_number } of messages) {
      void emitter(done, page_count)

      const { size, stream } = await RequestBlob(engine, content, emit)

      pages.push(content as SinglePagePdf)

      yield {
        page_number,
        size,
        stream,
        type: ContentType.PDF,
      }

      done += 1
      void emitter(done, page_count)
    }
  }

  const content = await GenerateFile(
    engine,
    PDFMerge(pagesGenerator()),
    ContentType.PDF,
    emit
  )

  yield {
    content: {
      content,
      enum: VirtualFileType.MULTI_PAGE_PDF,
      file_meta: {},
      pages: SortBy(pages, p => p.page_number),
    },
    enum: TaskName.CONTENT_FILE,
    name: "merge.pdf",
  } as TaskResult
}
